import { useState, useEffect, useCallback, useRef } from 'react'
import { fmtDate, fmtDateTime } from '../utils/format'
import SortableHeader, { useSort } from '../components/SortableHeader'
import {
  MagnifyingGlassIcon,
  PrinterIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  CubeIcon,
  BuildingStorefrontIcon,
  TruckIcon,
  BeakerIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'
const API_BASE_URL = '/api/v1/dev'

interface SearchResult {
  id: number
  type: 'inventory_batch' | 'production_output'
  code: string
  lot_number: string
  name: string
  ingredient_type: string
  supplier: string | null
  status: string
  quantity_available_g: number
  expiry_date: string | null
  created_at: string
}

interface OriginData {
  type: 'receipt' | 'production' | 'circular_ref'
  batch_id?: number
  // For receipt type (leaf node)
  receipt?: {
    id: number
    code: string
    received_at: string
    invoice_number: string | null
    delivery_note: string | null
    notes: string | null
    status: string
  }
  receipt_item?: {
    brand: string | null
    lot_number: string
    quantity: number
    unit: string
    unit_price: number
    total_price: number
    temperature_status: string
    expiry_date: string | null
    notes: string | null
  }
  purchase_order?: {
    id: number
    code: string
    created_at: string
    sent_at: string | null
  } | null
  supplier?: {
    id: number
    trade_name: string
    nif: string | null
    phone: string | null
    email: string | null
  }
  // For production type
  production_order?: {
    id: number
    code: string
    status: string
    recipe_name: string
    started_at: string | null
    completed_at: string | null
  } | null
  ingredients_consumed?: Array<{
    ingredient: {
      id: number
      name: string
      type: string
    }
    batch: {
      id: number
      internal_code: string
      lot_number: string
      status: string
    }
    quantity_consumed_g: number
    consumed_at: string
    supplier: string | null
    estimated?: boolean
    origin: OriginData | null
  }>
}

interface BatchTraceability {
  batch: {
    id: number
    internal_code: string
    lot_number: string
    status: string
    quantity_total_g: number
    quantity_available_g: number
    usage_percentage: number
    best_before: string | null
    expiry_date: string | null
    days_until_expiry: number | null
    opened_at: string | null
    closed_at: string | null
    location: string | null
    created_at: string
    is_production_batch?: boolean
  }
  ingredient: {
    id: number
    name: string
    type: string
    default_unit: string | null
    allergens: Array<{ id: number; name: string }>
  }
  supplier: {
    id: number
    trade_name: string
    is_self?: boolean
  } | null
  backward_traceability: OriginData | null
  forward_traceability: ForwardNode[]
  shipments?: ShipmentInfo[]
}

interface ShipmentInfo {
  shipment_id: number
  code: string
  invoice_number: string | null
  shipped_date: string | null
  quantity_g: number
}

interface ForwardNode {
  production_order: {
    id: number
    code: string
    status: string
    started_at: string | null
    completed_at: string | null
    recipe_name: string
    output_ingredient: string | null
  } | null
  quantity_consumed_g: number | null
  consumed_at: string | null
  estimated?: boolean
  outputs: Array<{
    id: number
    batch_code: string | null
    lot_number: string | null
    ingredient_name: string | null
    quantity_g: number
    status: string | null
    forward: ForwardNode[]
  }>
}

// ProductionTraceability interface removed - using BatchTraceability for all cases

interface InventoryBatchRow {
  id: number
  internal_code: string
  lot_number: string
  ingredient: { id: number; name: string; default_unit_name?: string } | null
  supplier: { id: number; trade_name: string } | null
  brand: string | null
  status: string
  quantity_total_g: number
  quantity_available_g: number
  expiry_date: string | null
  best_before: string | null
  days_until_expiry: number | null
  created_at: string
}

export default function Traceability() {
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [selectedResult, setSelectedResult] = useState<SearchResult | null>(null)
  const [batchTraceability, setBatchTraceability] = useState<BatchTraceability | null>(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [isLoadingDetail, setIsLoadingDetail] = useState(false)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    backward: false,
    forward: false,
    ingredients: false,
    allergens: false,
  })
  const [expandSignal, setExpandSignal] = useState(0)
  const [expandAllState, setExpandAllState] = useState(false)
  const searchTimeout = useRef<NodeJS.Timeout | null>(null)
  const printRef = useRef<HTMLDivElement>(null)

  // All batches table sorting
  const { sortField, sortDirection, handleSort, sortData } = useSort<InventoryBatchRow>('created_at', 'desc')

  // All batches table state
  const [allBatches, setAllBatches] = useState<InventoryBatchRow[]>([])
  const [batchesPage, setBatchesPage] = useState(1)
  const [batchesTotalPages, setBatchesTotalPages] = useState(1)
  const [batchesTotal, setBatchesTotal] = useState(0)
  const [batchesLoading, setBatchesLoading] = useState(true)

  const handleExpandAll = () => {
    setExpandedSections({ backward: true, forward: true, ingredients: true, allergens: true })
    setExpandAllState(true)
    setExpandSignal(prev => prev + 1)
  }

  const handleCollapseAll = () => {
    setExpandedSections({ backward: false, forward: false, ingredients: false, allergens: false })
    setExpandAllState(false)
    setExpandSignal(prev => prev + 1)
  }

  // Fetch batches on mount
  useEffect(() => {
    fetchAllBatches(1)
  }, [])

  // Fetch batches when page changes
  useEffect(() => {
    if (batchesPage > 1) {
      fetchAllBatches(batchesPage)
    }
  }, [batchesPage])

  const fetchAllBatches = async (page: number) => {
    try {
      setBatchesLoading(true)
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '15',
        sort_by: 'created_at',
        sort_order: 'desc'
      })
      const response = await fetch(`${API_BASE_URL}/inventory/batches?${params}`)
      const data = await response.json()
      if (response.ok) {
        setAllBatches(data.data)
        setBatchesPage(data.current_page)
        setBatchesTotalPages(data.last_page)
        setBatchesTotal(data.total)
      }
    } catch (error) {
      console.error('Error fetching batches:', error)
    } finally {
      setBatchesLoading(false)
    }
  }

  // Debounced search
  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value)

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current)
    }

    if (value.length < 2) {
      setSearchResults([])
      return
    }

    searchTimeout.current = setTimeout(async () => {
      setIsSearching(true)
      try {
        const response = await fetch(`${API_BASE_URL}/traceability/search?q=${encodeURIComponent(value)}`)
        const data = await response.json()
        setSearchResults(data.data)
      } catch (error) {
        console.error('Error searching:', error)
        setSearchResults([])
      } finally {
        setIsSearching(false)
      }
    }, 300)
  }, [])

  const handleSelectResult = async (result: SearchResult) => {
    setSelectedResult(result)
    setShowDetailModal(true)
    setIsLoadingDetail(true)
    setBatchTraceability(null)

    try {
      // All items are inventory batches now
      const response = await fetch(`${API_BASE_URL}/traceability/batch/${result.id}`)
      const data = await response.json()
      setBatchTraceability(data.data)
    } catch (error) {
      console.error('Error fetching traceability:', error)
    } finally {
      setIsLoadingDetail(false)
    }
  }

  const handleSelectBatchRow = (batch: InventoryBatchRow) => {
    const result: SearchResult = {
      id: batch.id,
      type: 'inventory_batch',
      code: batch.internal_code,
      lot_number: batch.lot_number,
      name: batch.ingredient?.name || 'N/A',
      ingredient_type: 'normal',
      supplier: batch.supplier?.trade_name || null,
      status: batch.status,
      quantity_available_g: batch.quantity_available_g,
      expiry_date: batch.expiry_date,
      created_at: batch.created_at,
    }
    handleSelectResult(result)
  }

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'unopened':
        return 'bg-blue-100 text-blue-800'
      case 'open':
        return 'bg-green-100 text-green-800'
      case 'depleted':
        return 'bg-gray-100 text-gray-800'
      case 'expired':
        return 'bg-red-100 text-red-800'
      case 'completed':
        return 'bg-green-100 text-green-800'
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'unopened':
        return 'Sin abrir'
      case 'open':
        return 'Abierto'
      case 'depleted':
        return 'Agotado'
      case 'expired':
        return 'Vencido'
      case 'completed':
        return 'Completado'
      case 'in_progress':
        return 'En progreso'
      case 'pending':
        return 'Pendiente'
      case 'cancelled':
        return 'Cancelado'
      case 'unknown':
        return 'Desconocido'
      default:
        return status
    }
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'normal':
        return <CubeIcon className="h-5 w-5 text-blue-500" />
      case 'elaborated':
        return <BeakerIcon className="h-5 w-5 text-yellow-500" />
      case 'finished_product':
        return <BuildingStorefrontIcon className="h-5 w-5 text-green-500" />
      case 'production_output':
        return <BeakerIcon className="h-5 w-5 text-purple-500" />
      default:
        return <CubeIcon className="h-5 w-5 text-gray-500" />
    }
  }

  const getTypeText = (type: string) => {
    switch (type) {
      case 'normal':
        return 'Ingrediente'
      case 'elaborated':
        return 'Elaborado'
      case 'finished_product':
        return 'Producto Terminado'
      case 'pack':
        return 'Pack'
      case 'production_output':
        return 'Producción'
      default:
        return type
    }
  }

  const formatQuantity = (grams: number | string | null | undefined) => {
    if (grams == null) return 'N/A'
    const g = typeof grams === 'string' ? parseFloat(grams) : grams
    if (isNaN(g)) return '0 g'
    if (g >= 1000) {
      return `${(g / 1000).toFixed(2)} kg`
    }
    return `${g.toFixed(0)} g`
  }

  const handlePrint = () => {
    window.print()
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold leading-6 text-gray-900">
            Sistema de Trazabilidad
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Trazabilidad completa bidireccional de todos los lotes e ingredientes.
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="mt-8">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            {isSearching ? (
              <ArrowPathIcon className="h-5 w-5 text-gray-400 animate-spin" />
            ) : (
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            )}
          </div>
          <input
            type="text"
            placeholder="Buscar por código de lote o código interno (mínimo 2 caracteres)..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-lg"
          />
        </div>
      </div>

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="mt-4 bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="text-sm font-medium text-gray-900">
              Resultados de búsqueda ({searchResults.length})
            </h3>
          </div>
          <ul className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {searchResults.map((result) => (
              <li
                key={`${result.type}-${result.id}`}
                className="px-4 py-4 hover:bg-gray-50 cursor-pointer"
                onClick={() => handleSelectResult(result)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {getTypeIcon(result.ingredient_type)}
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">
                        <span className="font-mono">{result.code}</span>
                        {result.lot_number && result.lot_number !== result.code && (
                          <span className="ml-2 text-gray-500">
                            (Lote: {result.lot_number})
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-gray-500">
                        {result.name}
                        <span className="ml-2 text-xs bg-gray-100 px-1 rounded">{getTypeText(result.ingredient_type)}</span>
                        {result.supplier && (
                          <span className="ml-2">• {result.supplier}</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm text-gray-500">
                      {formatQuantity(result.quantity_available_g)}
                    </span>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(result.status)}`}>
                      {getStatusText(result.status)}
                    </span>
                    <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {searchTerm.length >= 2 && searchResults.length === 0 && !isSearching && (
        <div className="mt-4 bg-white shadow rounded-lg p-8 text-center">
          <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Sin resultados</h3>
          <p className="mt-1 text-sm text-gray-500">
            No se encontraron lotes con el código "{searchTerm}"
          </p>
        </div>
      )}

      {/* All Batches Table */}
      <div className="mt-8 bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Todos los Lotes ({batchesTotal})
          </h3>
        </div>
        {batchesLoading && allBatches.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <ArrowPathIcon className="h-8 w-8 text-primary-500 animate-spin" />
            <span className="ml-2 text-gray-500">Cargando lotes...</span>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortableHeader label="Código" field="internal_code" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                    <SortableHeader label="Ingrediente" field="ingredient.name" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                    <SortableHeader label="Proveedor" field="supplier.trade_name" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                    <SortableHeader label="Estado" field="status" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                    <SortableHeader label="Cantidad" field="quantity_available_g" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                    <SortableHeader label="Caducidad" field="expiry_date" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                    <SortableHeader label="Fecha Creación" field="created_at" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortData(allBatches).map((batch) => (
                    <tr
                      key={batch.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleSelectBatchRow(batch)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono font-medium text-gray-900">{batch.internal_code}</div>
                        <div className="text-xs text-gray-500 font-mono">{batch.lot_number}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {batch.ingredient?.name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {batch.supplier?.trade_name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(batch.status)}`}>
                          {getStatusText(batch.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatQuantity(batch.quantity_available_g)} / {formatQuantity(batch.quantity_total_g)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {batch.best_before || batch.expiry_date ? fmtDate(batch.best_before || batch.expiry_date!) : '-'}
                        {batch.days_until_expiry !== null && batch.days_until_expiry <= 7 && (
                          <span className={`ml-1 text-xs ${batch.days_until_expiry <= 0 ? 'text-red-600' : batch.days_until_expiry <= 3 ? 'text-red-500' : 'text-orange-500'}`}>
                            ({batch.days_until_expiry <= 0 ? 'Vencido' : `${batch.days_until_expiry}d`})
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {fmtDate(batch.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {batchesTotalPages > 1 && (
              <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Página {batchesPage} de {batchesTotalPages} ({batchesTotal} lotes)
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setBatchesPage(prev => Math.max(1, prev - 1))}
                      disabled={batchesPage === 1}
                      className="px-3 py-1 border rounded-md text-sm disabled:opacity-50 hover:bg-gray-50"
                    >
                      Anterior
                    </button>
                    <button
                      onClick={() => setBatchesPage(prev => Math.min(batchesTotalPages, prev + 1))}
                      disabled={batchesPage === batchesTotalPages}
                      className="px-3 py-1 border rounded-md text-sm disabled:opacity-50 hover:bg-gray-50"
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 print:bg-white print:relative print:overflow-visible">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white print:shadow-none print:border-none print:top-0 print:max-w-none print:w-full" ref={printRef}>
            <div className="mt-3">
              {/* Header */}
              <div className="flex items-center justify-between mb-6 print:mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    Informe de Trazabilidad
                  </h3>
                  <p className="text-sm text-gray-500">
                    {selectedResult?.code} • {selectedResult?.name}
                  </p>
                </div>
                <div className="flex items-center space-x-2 print:hidden">
                  <button
                    onClick={handleExpandAll}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <ChevronDownIcon className="h-4 w-4 mr-1" />
                    Expandir todo
                  </button>
                  <button
                    onClick={handleCollapseAll}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <ChevronRightIcon className="h-4 w-4 mr-1" />
                    Colapsar todo
                  </button>
                  <button
                    onClick={handlePrint}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <PrinterIcon className="h-4 w-4 mr-2" />
                    Imprimir
                  </button>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <span className="sr-only">Cerrar</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {isLoadingDetail ? (
                <div className="flex items-center justify-center py-12">
                  <ArrowPathIcon className="h-8 w-8 text-primary-500 animate-spin" />
                  <span className="ml-2 text-gray-500">Cargando trazabilidad...</span>
                </div>
              ) : batchTraceability ? (
                // Batch Traceability View (handles both receipt and production batches)
                <BatchTraceabilityView
                  data={batchTraceability}
                  expandedSections={expandedSections}
                  toggleSection={toggleSection}
                  formatQuantity={formatQuantity}
                  getStatusColor={getStatusColor}
                  getStatusText={getStatusText}
                  expandSignal={expandSignal}
                  expandAllState={expandAllState}
                />
              ) : (
                <div className="text-center py-12 text-gray-500">
                  No se pudo cargar la información de trazabilidad
                </div>
              )}

              {/* Footer */}
              <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between items-center print:mt-8">
                <p className="text-xs text-gray-500">
                  Generado el {fmtDateTime(new Date())}
                </p>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 print:hidden"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .fixed {
            position: relative !important;
            top: 0 !important;
          }
          .fixed > div {
            visibility: visible;
            position: relative;
          }
          .fixed > div * {
            visibility: visible;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  )
}

// Batch Traceability View Component
function BatchTraceabilityView({
  data,
  expandedSections,
  toggleSection,
  formatQuantity,
  getStatusColor,
  getStatusText,
  expandSignal,
  expandAllState,
}: {
  data: BatchTraceability
  expandedSections: Record<string, boolean>
  toggleSection: (section: string) => void
  formatQuantity: (grams: number | string | null | undefined) => string
  getStatusColor: (status: string) => string
  getStatusText: (status: string) => string
  expandSignal: number
  expandAllState: boolean
}) {
  return (
    <div className="space-y-6">
      {/* Batch Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
            <CubeIcon className="h-5 w-5 mr-2 text-blue-500" />
            Información del Lote
          </h4>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-500">Código interno:</dt>
              <dd className="font-mono font-medium">{data.batch.internal_code}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Número de lote:</dt>
              <dd className="font-mono">{data.batch.lot_number}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Estado:</dt>
              <dd>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(data.batch.status)}`}>
                  {getStatusText(data.batch.status)}
                </span>
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Cantidad total:</dt>
              <dd className="font-medium">{formatQuantity(parseFloat(data.batch.quantity_total_g.toString()))}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Cantidad disponible:</dt>
              <dd className="font-medium">{formatQuantity(parseFloat(data.batch.quantity_available_g.toString()))}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Uso:</dt>
              <dd>{parseFloat(String(data.batch.usage_percentage)).toFixed(1)}%</dd>
            </div>
            {data.batch.expiry_date && (
              <div className="flex justify-between">
                <dt className="text-gray-500">Caducidad:</dt>
                <dd className={data.batch.days_until_expiry !== null && data.batch.days_until_expiry <= 7 ? 'text-red-600 font-medium' : ''}>
                  {fmtDate(data.batch.expiry_date)}
                  {data.batch.days_until_expiry !== null && (
                    <span className="ml-1">({data.batch.days_until_expiry} días)</span>
                  )}
                </dd>
              </div>
            )}
            {data.batch.location && (
              <div className="flex justify-between">
                <dt className="text-gray-500">Ubicación:</dt>
                <dd>{data.batch.location}</dd>
              </div>
            )}
            <div className="flex justify-between">
              <dt className="text-gray-500">Creado:</dt>
              <dd>{data.batch.created_at}</dd>
            </div>
          </dl>
        </div>

        <div className="bg-blue-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
            <BeakerIcon className="h-5 w-5 mr-2 text-blue-500" />
            Ingrediente
          </h4>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-500">Nombre:</dt>
              <dd className="font-medium">{data.ingredient.name}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Tipo:</dt>
              <dd>{data.ingredient.type}</dd>
            </div>
            {data.ingredient.default_unit && (
              <div className="flex justify-between">
                <dt className="text-gray-500">Unidad:</dt>
                <dd>{data.ingredient.default_unit}</dd>
              </div>
            )}
          </dl>

          {data.ingredient.allergens && data.ingredient.allergens.length > 0 && (
            <div className="mt-4">
              <h5 className="text-xs font-medium text-gray-700 mb-2">Alérgenos:</h5>
              <div className="flex flex-wrap gap-1">
                {data.ingredient.allergens.map(allergen => (
                  <span
                    key={allergen.id}
                    className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800"
                  >
                    {allergen.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {data.supplier && (
          <div className="bg-green-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
              <TruckIcon className="h-5 w-5 mr-2 text-green-500" />
              Proveedor
            </h4>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Nombre:</dt>
                <dd className="font-medium">{data.supplier.trade_name}</dd>
              </div>
            </dl>
          </div>
        )}
      </div>

      {/* Backward Traceability - Recursive Tree */}
      {data.backward_traceability && (
        <div className="border rounded-lg">
          <button
            onClick={() => toggleSection('backward')}
            className="flex items-center justify-between w-full px-4 py-3 text-left bg-blue-50 rounded-t-lg"
          >
            <div className="flex items-center">
              {expandedSections.backward ? (
                <ChevronDownIcon className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronRightIcon className="h-5 w-5 text-gray-500" />
              )}
              <span className="ml-2 text-sm font-medium text-gray-900">
                Trazabilidad hacia Atrás (Origen completo)
              </span>
            </div>
          </button>

          {expandedSections.backward && (
            <div className="p-4">
              <OriginNode
                origin={data.backward_traceability}
                formatQuantity={formatQuantity}
                getStatusColor={getStatusColor}
                getStatusText={getStatusText}
                depth={0}
                expandSignal={expandSignal}
                expandAllState={expandAllState}
              />
            </div>
          )}
        </div>
      )}

      {/* Forward Traceability */}
      {data.forward_traceability.length > 0 && (
        <div className="border rounded-lg">
          <button
            onClick={() => toggleSection('forward')}
            className="flex items-center justify-between w-full px-4 py-3 text-left bg-green-50 rounded-t-lg"
          >
            <div className="flex items-center">
              {expandedSections.forward ? (
                <ChevronDownIcon className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronRightIcon className="h-5 w-5 text-gray-500" />
              )}
              <span className="ml-2 text-sm font-medium text-gray-900">
                Trazabilidad hacia Adelante (Destino)
              </span>
            </div>
            <span className="text-xs text-gray-500">{data.forward_traceability.length} producciones</span>
          </button>

          {expandedSections.forward && (
            <div className="p-4 space-y-4">
              {data.forward_traceability.map((item, index) => (
                <ForwardTraceNode
                  key={index}
                  node={item}
                  formatQuantity={formatQuantity}
                  getStatusColor={getStatusColor}
                  getStatusText={getStatusText}
                  depth={0}
                  expandSignal={expandSignal}
                  expandAllState={expandAllState}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Salidas a venta (expediciones) */}
      {data.shipments && data.shipments.length > 0 && (
        <div className="border rounded-lg">
          <div className="px-4 py-3 bg-amber-50 rounded-t-lg flex items-center justify-between">
            <span className="text-sm font-medium text-gray-900">Salidas a venta (Expediciones)</span>
            <span className="text-xs text-gray-500">{data.shipments.length} expedición(es)</span>
          </div>
          <div className="p-4 space-y-2">
            {data.shipments.map((s) => (
              <div key={s.shipment_id} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm border-b border-gray-100 pb-2 last:border-0">
                <span className="font-mono font-medium text-gray-900">{s.code}</span>
                <span className="text-gray-600">Factura/Albarán: <span className="text-gray-900">{s.invoice_number || '-'}</span></span>
                <span className="text-gray-600">Fecha: <span className="text-gray-900">{s.shipped_date ? fmtDate(s.shipped_date) : '-'}</span></span>
                <span className="text-gray-600">Cantidad: <span className="text-gray-900">{formatQuantity(s.quantity_g)}</span></span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// Recursive Origin Node Component
function OriginNode({
  origin,
  formatQuantity,
  getStatusColor,
  getStatusText,
  depth = 0,
  expandSignal,
  expandAllState,
}: {
  origin: OriginData
  formatQuantity: (grams: number | string | null | undefined) => string
  getStatusColor: (status: string) => string
  getStatusText: (status: string) => string
  depth?: number
  expandSignal: number
  expandAllState: boolean
}) {
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    if (expandSignal > 0) {
      setExpanded(expandAllState)
    }
  }, [expandSignal, expandAllState])

  const depthColors = [
    'border-blue-400',
    'border-purple-400',
    'border-indigo-400',
    'border-cyan-400',
    'border-teal-400',
  ]
  const borderColor = depthColors[depth % depthColors.length]

  if (origin.type === 'circular_ref') {
    return (
      <div className="text-xs text-gray-400 italic p-2 bg-gray-50 rounded">
        (Referencia circular - lote ya mostrado)
      </div>
    )
  }

  if (origin.type === 'production') {
    const po = origin.production_order
    if (!po) {
      return (
        <div className="text-xs text-gray-400 italic p-2">
          Orden de producción no encontrada
        </div>
      )
    }

    return (
      <div className={`border-l-4 ${borderColor} pl-3 mt-2`}>
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center w-full text-left group hover:bg-gray-50 rounded p-1 -ml-1"
        >
          {expanded ? (
            <ChevronDownIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
          ) : (
            <ChevronRightIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
          )}
          <BeakerIcon className="h-4 w-4 text-purple-500 ml-1 flex-shrink-0" />
          <span className="ml-2 text-sm font-medium text-purple-700">
            Producción: {po.code}
          </span>
          <span className="ml-2 text-xs text-gray-500">
            {po.recipe_name}
          </span>
          <span className={`ml-2 inline-flex px-1.5 py-0.5 text-xs rounded-full ${getStatusColor(po.status)}`}>
            {getStatusText(po.status)}
          </span>
          {origin.ingredients_consumed && (
            <span className="ml-auto text-xs text-gray-400">
              {origin.ingredients_consumed.length} ingredientes
            </span>
          )}
        </button>

        {expanded && (
          <div className="mt-2 ml-5 space-y-1">
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-3 bg-purple-50 p-2 rounded">
              {po.started_at && (
                <div>
                  <span className="text-gray-400">Inicio:</span> {po.started_at}
                </div>
              )}
              {po.completed_at && (
                <div>
                  <span className="text-gray-400">Fin:</span> {po.completed_at}
                </div>
              )}
            </div>

            {origin.ingredients_consumed && origin.ingredients_consumed.length > 0 && (
              <div>
                <h6 className="text-xs font-medium text-gray-700 mb-2">
                  Ingredientes consumidos:
                </h6>
                <div className="space-y-2">
                  {origin.ingredients_consumed.map((item, index) => (
                    <IngredientConsumedNode
                      key={`${item.batch.id}-${index}`}
                      item={item}
                      formatQuantity={formatQuantity}
                      getStatusColor={getStatusColor}
                      getStatusText={getStatusText}
                      depth={depth + 1}
                      expandSignal={expandSignal}
                      expandAllState={expandAllState}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  if (origin.type === 'receipt') {
    return (
      <div className={`border-l-4 border-green-400 pl-3 mt-2`}>
        <div className="flex items-center p-1">
          <TruckIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
          <span className="ml-2 text-sm font-medium text-green-700">
            Recepción: {origin.receipt?.code || 'N/A'}
          </span>
          {origin.receipt?.received_at && (
            <span className="ml-2 text-xs text-gray-500">
              {origin.receipt.received_at}
            </span>
          )}
        </div>
        <div className="ml-5 mt-1 space-y-2">
          {/* Receipt details */}
          <div className="bg-green-50 p-2 rounded text-xs">
            <div className="grid grid-cols-2 gap-2">
              {origin.supplier && (
                <div>
                  <span className="text-gray-400">Proveedor:</span>{' '}
                  <span className="font-medium">{origin.supplier.trade_name}</span>
                  {origin.supplier.nif && (
                    <span className="text-gray-400 ml-1">({origin.supplier.nif})</span>
                  )}
                </div>
              )}
              {origin.receipt_item?.brand && (
                <div>
                  <span className="text-gray-400">Marca:</span> {origin.receipt_item.brand}
                </div>
              )}
              {origin.receipt_item?.lot_number && (
                <div>
                  <span className="text-gray-400">Lote proveedor:</span>{' '}
                  <span className="font-mono">{origin.receipt_item.lot_number}</span>
                </div>
              )}
              {origin.receipt_item?.quantity != null && (
                <div>
                  <span className="text-gray-400">Cantidad recibida:</span>{' '}
                  {origin.receipt_item.quantity} {origin.receipt_item.unit}
                </div>
              )}
              {origin.receipt_item?.unit_price != null && (
                <div>
                  <span className="text-gray-400">Precio unitario:</span>{' '}
                  {parseFloat(String(origin.receipt_item.unit_price)).toFixed(2)} EUR
                </div>
              )}
              {origin.receipt_item?.temperature_status && (
                <div>
                  <span className="text-gray-400">Temperatura:</span>{' '}
                  <span className={origin.receipt_item.temperature_status === 'ok' ? 'text-green-600' : 'text-red-600'}>
                    {origin.receipt_item.temperature_status === 'ok' ? 'OK' : origin.receipt_item.temperature_status}
                  </span>
                </div>
              )}
              {origin.receipt?.invoice_number && (
                <div>
                  <span className="text-gray-400">Factura:</span> {origin.receipt.invoice_number}
                </div>
              )}
              {origin.receipt?.delivery_note && (
                <div>
                  <span className="text-gray-400">Albarán:</span> {origin.receipt.delivery_note}
                </div>
              )}
            </div>
          </div>

          {/* Purchase order */}
          {origin.purchase_order && (
            <div className="bg-yellow-50 p-2 rounded text-xs">
              <div className="flex items-center mb-1">
                <span className="font-medium text-yellow-700">
                  Orden de compra: {origin.purchase_order.code}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {origin.purchase_order.created_at && (
                  <div>
                    <span className="text-gray-400">Creada:</span> {origin.purchase_order.created_at}
                  </div>
                )}
                {origin.purchase_order.sent_at && (
                  <div>
                    <span className="text-gray-400">Enviada:</span> {origin.purchase_order.sent_at}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  return null
}

// Ingredient Consumed Node (used inside OriginNode for production)
function IngredientConsumedNode({
  item,
  formatQuantity,
  getStatusColor,
  getStatusText,
  depth = 0,
  expandSignal,
  expandAllState,
}: {
  item: {
    ingredient: { id: number; name: string; type: string }
    batch: { id: number; internal_code: string; lot_number: string; status: string }
    quantity_consumed_g: number
    consumed_at: string
    supplier: string | null
    estimated?: boolean
    origin: OriginData | null
  }
  formatQuantity: (grams: number | string | null | undefined) => string
  getStatusColor: (status: string) => string
  getStatusText: (status: string) => string
  depth?: number
  expandSignal: number
  expandAllState: boolean
}) {
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    if (expandSignal > 0) {
      setExpanded(expandAllState)
    }
  }, [expandSignal, expandAllState])
  const hasOrigin = item.origin !== null

  const typeLabel = item.ingredient.type === 'elaborated' ? 'Elaborado' :
                    item.ingredient.type === 'finished_product' ? 'Producto' : 'Ingrediente'
  const typeColor = item.ingredient.type === 'elaborated' ? 'text-yellow-600 bg-yellow-50' :
                    item.ingredient.type === 'finished_product' ? 'text-green-600 bg-green-50' : 'text-blue-600 bg-blue-50'

  return (
    <div className={`rounded p-2 ${item.estimated ? 'bg-amber-50 border border-dashed border-amber-300' : 'bg-gray-50'}`}>
      <div
        className={`flex items-center justify-between ${hasOrigin ? 'cursor-pointer hover:bg-gray-100 rounded' : ''}`}
        onClick={() => hasOrigin && setExpanded(!expanded)}
      >
        <div className="flex items-center">
          {hasOrigin ? (
            expanded ? (
              <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            ) : (
              <ChevronRightIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            )
          ) : (
            <div className="w-3.5" />
          )}
          <span className="ml-1 text-sm font-medium text-gray-900">
            {item.ingredient.name}
          </span>
          <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${typeColor}`}>
            {typeLabel}
          </span>
          {item.estimated && (
            <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
              Estimado
            </span>
          )}
        </div>
        <div className="flex items-center space-x-3 text-xs">
          <span className="font-mono text-gray-500">{item.batch.internal_code}</span>
          <span className="text-gray-500">{item.batch.lot_number}</span>
          <span className="font-medium">{formatQuantity(item.quantity_consumed_g)}</span>
          <span className={`inline-flex px-1.5 py-0.5 rounded-full ${getStatusColor(item.batch.status)}`}>
            {getStatusText(item.batch.status)}
          </span>
          {item.supplier && (
            <span className="text-gray-400">{item.supplier}</span>
          )}
        </div>
      </div>

      {/* Recursive origin */}
      {hasOrigin && expanded && item.origin && (
        <div className="mt-2 ml-4">
          <OriginNode
            origin={item.origin}
            formatQuantity={formatQuantity}
            getStatusColor={getStatusColor}
            getStatusText={getStatusText}
            depth={depth}
            expandSignal={expandSignal}
            expandAllState={expandAllState}
          />
        </div>
      )}
    </div>
  )
}

// Recursive Forward Traceability Node
function ForwardTraceNode({
  node,
  formatQuantity,
  getStatusColor,
  getStatusText,
  depth = 0,
  expandSignal,
  expandAllState,
}: {
  node: ForwardNode
  formatQuantity: (grams: number | string | null | undefined) => string
  getStatusColor: (status: string) => string
  getStatusText: (status: string) => string
  depth?: number
  expandSignal: number
  expandAllState: boolean
}) {
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    if (expandSignal > 0) {
      setExpanded(expandAllState)
    }
  }, [expandSignal, expandAllState])

  const depthColors = [
    'border-green-400',
    'border-emerald-400',
    'border-teal-400',
    'border-lime-400',
    'border-green-300',
  ]
  const borderColor = depthColors[depth % depthColors.length]

  const po = node.production_order
  const hasChildren = node.outputs.some(o => o.forward && o.forward.length > 0)

  if (!po) {
    return (
      <div className="text-xs text-gray-400 italic p-2">
        Orden de producción no encontrada
      </div>
    )
  }

  return (
    <div className={`border-l-4 ${borderColor} pl-3 mt-2 ${node.estimated ? 'bg-amber-50/50' : ''}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center w-full text-left group hover:bg-gray-50 rounded p-1 -ml-1"
      >
        {expanded ? (
          <ChevronDownIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronRightIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
        )}
        <BeakerIcon className="h-4 w-4 text-green-500 ml-1 flex-shrink-0" />
        <span className="ml-2 text-sm font-medium text-green-700">
          Producción: {po.code}
        </span>
        <span className="ml-2 text-xs text-gray-500">
          {po.recipe_name}
        </span>
        <span className={`ml-2 inline-flex px-1.5 py-0.5 text-xs rounded-full ${getStatusColor(po.status)}`}>
          {getStatusText(po.status)}
        </span>
        {node.estimated && (
          <span className="ml-2 inline-flex px-1.5 py-0.5 text-xs rounded bg-amber-100 text-amber-700">
            Estimado
          </span>
        )}
        <span className="ml-2 text-xs text-gray-400">
          ({formatQuantity(node.quantity_consumed_g)} consumido)
        </span>
        {hasChildren && (
          <span className="ml-auto text-xs text-green-500 font-medium">
            Continúa →
          </span>
        )}
      </button>

      {expanded && (
        <div className="mt-2 ml-5 space-y-2">
          <div className={`grid grid-cols-2 gap-2 text-xs text-gray-600 mb-2 p-2 rounded ${node.estimated ? 'bg-amber-50 border border-dashed border-amber-300' : 'bg-green-50'}`}>
            <div>
              <span className="text-gray-400">Producto:</span>{' '}
              {po.output_ingredient || 'N/A'}
            </div>
            <div>
              <span className="text-gray-400">Consumido:</span> {node.consumed_at || 'N/A'}
            </div>
            {po.started_at && (
              <div>
                <span className="text-gray-400">Inicio:</span> {po.started_at}
              </div>
            )}
            {po.completed_at && (
              <div>
                <span className="text-gray-400">Fin:</span> {po.completed_at}
              </div>
            )}
          </div>

          {node.outputs.length > 0 && (
            <div>
              <h6 className="text-xs font-medium text-gray-700 mb-1">Lotes producidos:</h6>
              <div className="space-y-2">
                {node.outputs.map(output => (
                  <OutputBatchNode
                    key={output.id}
                    output={output}
                    formatQuantity={formatQuantity}
                    getStatusColor={getStatusColor}
                    getStatusText={getStatusText}
                    depth={depth}
                    expandSignal={expandSignal}
                    expandAllState={expandAllState}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Output Batch Node (shows batch and its recursive forward chain)
function OutputBatchNode({
  output,
  formatQuantity,
  getStatusColor,
  getStatusText,
  depth,
  expandSignal,
  expandAllState,
}: {
  output: {
    id: number
    batch_code: string | null
    lot_number: string | null
    ingredient_name: string | null
    quantity_g: number
    status: string | null
    forward: ForwardNode[]
  }
  formatQuantity: (grams: number | string | null | undefined) => string
  getStatusColor: (status: string) => string
  getStatusText: (status: string) => string
  depth: number
  expandSignal: number
  expandAllState: boolean
}) {
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    if (expandSignal > 0) {
      setExpanded(expandAllState)
    }
  }, [expandSignal, expandAllState])
  const hasForward = output.forward && output.forward.length > 0

  return (
    <div className="bg-gray-50 rounded p-2">
      <div
        className={`flex items-center justify-between ${hasForward ? 'cursor-pointer hover:bg-gray-100 rounded' : ''}`}
        onClick={() => hasForward && setExpanded(!expanded)}
      >
        <div className="flex items-center text-xs">
          {hasForward ? (
            expanded ? (
              <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            ) : (
              <ChevronRightIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            )
          ) : (
            <div className="w-3.5" />
          )}
          <span className="ml-1 font-mono font-medium">{output.batch_code || 'N/A'}</span>
          {output.ingredient_name && (
            <span className="ml-2 text-gray-500">{output.ingredient_name}</span>
          )}
        </div>
        <div className="flex items-center space-x-3 text-xs">
          <span>{formatQuantity(output.quantity_g)}</span>
          {output.status && (
            <span className={`inline-flex px-1.5 py-0.5 rounded-full ${getStatusColor(output.status)}`}>
              {getStatusText(output.status)}
            </span>
          )}
          {hasForward && (
            <span className="text-green-500 text-xs font-medium">
              → {output.forward.length} prod.
            </span>
          )}
        </div>
      </div>

      {/* Recursive forward chain */}
      {hasForward && expanded && (
        <div className="mt-2 ml-4 space-y-2">
          {output.forward.map((fwd, i) => (
            <ForwardTraceNode
              key={i}
              node={fwd}
              formatQuantity={formatQuantity}
              getStatusColor={getStatusColor}
              getStatusText={getStatusText}
              depth={depth + 1}
              expandSignal={expandSignal}
              expandAllState={expandAllState}
            />
          ))}
        </div>
      )}
    </div>
  )
}
